import React from 'react'
import { Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { router, usePage } from '@inertiajs/react'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import StatusBadge from '../../../../components/ui/StatusBadge'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'

Show.layout = (page) => <AppShell>{page}</AppShell>

const PURPOSE_LABELS = {
  hausgeld: 'Hausgeld',
  reserve: 'Rücklage',
  rent: 'Miete',
  deposit: 'Kaution',
}

export default function Show({ property, bank_account, payments, filters = {} }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}`

  const applyFilter = (key, value) => {
    const params = new URLSearchParams()
    const newFilters = { ...filters, [key]: value }
    Object.entries(newFilters).forEach(([k, v]) => { if (v) params.set(k, v) })
    router.visit(`${basePath}/bank_accounts/${bank_account.id}?${params.toString()}`)
  }

  return (
    <Box>
      <PageHeader
        title={bank_account.account_holder || bank_account.iban}
        breadcrumbs={[
          { label: property.name, href: basePath },
          { label: 'Bankkonten', href: `${basePath}/bank_accounts` },
          { label: bank_account.account_holder || bank_account.iban },
        ]}
        actions={
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => router.visit(`${basePath}/payments/new?bank_account_id=${bank_account.id}`)}
            data-testid="new-payment-btn"
          >
            Neue Zahlung
          </Button>
        }
      />

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">IBAN</Typography>
            <Typography variant="body2" data-testid="bank-iban">{bank_account.iban}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Verwendungszweck</Typography>
            <Typography variant="body2">{PURPOSE_LABELS[bank_account.purpose] || bank_account.purpose || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Aktueller Kontostand</Typography>
            <Typography variant="body2" data-testid="bank-balance">
              <FormattedCurrency value={bank_account.current_balance} />
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Richtung</InputLabel>
          <Select value={filters.direction || ''} label="Richtung"
            onChange={(e) => applyFilter('direction', e.target.value)}
            data-testid="direction-filter"
          >
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="inbound">Eingang</MenuItem>
            <MenuItem value="outbound">Ausgang</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filters.status || ''} label="Status"
            onChange={(e) => applyFilter('status', e.target.value)}
            data-testid="status-filter"
          >
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="open">Offen</MenuItem>
            <MenuItem value="allocated">Zugeordnet</MenuItem>
            <MenuItem value="partially_allocated">Teilzugeordnet</MenuItem>
            <MenuItem value="cancelled">Storniert</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>Datum</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Richtung</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Kontakt</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Verwendungszweck</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Betrag</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Keine Zahlungen vorhanden.</Typography>
                </TableCell>
              </TableRow>
            )}
            {payments.map((p) => (
              <TableRow key={p.id} hover sx={{ cursor: 'pointer' }}
                onClick={() => router.visit(`${basePath}/payments/${p.id}`)}
              >
                <TableCell>{p.booking_date}</TableCell>
                <TableCell><StatusBadge type="payment_direction" value={p.direction} /></TableCell>
                <TableCell>{p.contact_name}</TableCell>
                <TableCell>{p.reference_text}</TableCell>
                <TableCell align="right"><FormattedCurrency value={p.amount} /></TableCell>
                <TableCell><StatusBadge type="payment_status" value={p.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
